<template>
    <Page header="Верификация" type="" :nopadding="false" header_pos="center">
        <template #body>
            <div class="flex flex-row w-full justify-content-center mb-5">
                <div class="flex flex-column gap-4 w-full max-w-30rem">
                    <!-- Индикатор шагов -->
                    <div v-if="step!=0" class="w-full flex flex-row align-items-center justify-content-between gap-3">
                        <div class="flex"></div>
                        <Knob v-model="percent" :strokeWidth="5" :size="50" class="flex" readonly valueColor="blue"/>
                        <div class="flex"></div>
                    </div>

                    <!-- Шаг 0: проверка статуса KYC -->
                    <div v-if="step===0" class="flex flex-column w-full gap-3">
                        <span class="flex text-md font-medium">Проверка статуса</span>

                        <div class="flex flex-column gap-2 p-3 border-1 surface-border border-round-lg">
                            <div class="flex flex-row justify-content-between">
                                <span class="text-600">Имя</span>
                                <span class="text-900">{{ client.first_name }}</span>
                            </div>
                            <div class="flex flex-row justify-content-between">
                                <span class="text-600">Фамилия</span>
                                <span class="text-900">{{ client.last_name }}</span>
                            </div>
                            <div class="flex flex-row justify-content-between">
                                <span class="text-600">Email</span>
                                <span class="text-900">{{ client.email }}</span>
                            </div>
                            <div class="flex flex-row justify-content-between">
                                <span class="text-600">Телефон</span>
                                <span class="text-900">{{ client.phone }}</span>
                            </div>
                            <Divider/>
                            <div class="flex flex-row justify-content-between">
                                <span class="text-600">Фото паспорта</span>
                                <span class="text-900" :class="(selectedDocumentId) ? 'text-green-600' : 'text-red-600'">
                                    {{ (selectedDocumentId) ? 'ок' : 'не загружен' }}
                                </span>
                            </div>
                            <div class="flex flex-row justify-content-between">
                                <span class="text-600">Фото лица</span>
                                <span class="text-900" :class="(selectedFaceId) ? 'text-green-600' : 'text-red-600'">
                                    {{ (selectedFaceId) ? 'ок' : 'не загружен' }}
                                </span>
                            </div>
                            <div v-if="verificationType=='avangard'" class="flex flex-row justify-content-between">
                                <span class="text-600">Фото прописки</span>
                                <span class="text-900" :class="(selectedRegistrationId) ? 'text-green-600' : 'text-red-600'">
                                    {{ (selectedRegistrationId) ? 'ок' : 'не загружен' }}
                                </span>
                            </div>
                        </div>
                        <div class="flex flex-column gap-2 p-3 border-1 surface-border border-round-lg">
                            <span v-if="kycStatusLoading" class="text-sm text-primary">Проверяем статус...</span>
                            <div v-else-if="kycPositive" class="flex flex-row gap-2">
                                <span class="flex">Статус: </span> 
                                <Tag :severity="getKycSeverity(kycStatus)" class="text-xs">
                                    {{ getKycStatus(kycStatus) }}
                                </Tag>
                                <Message v-if="kycErrorMessage" severity="error" size="small" icon="pi pi-exclamation-triangle" class="flex">
                                    {{ kycErrorMessage }}
                                </Message>
                            </div>
                            <div v-else class="flex flex-row gap-2 align-items-center">
                                <span class="flex">Статус: </span> 
                                <Tag :severity="getKycSeverity(kycStatus)" class="flex text-xs">
                                    {{ getKycStatus(kycStatus) }}
                                </Tag>
                                <Button v-if="kycPositive" label="Обновить статус" severity="secondary" outlined icon="pi pi-replay"
                                    @click="CheckKycStatus" :disabled="loading || kycStatusLoading"/>
                                <Button v-else label="Отправить" icon="pi pi-arrow-right" class="flex w-full border-round-lg flex-1" severity="success" iconPos="right" size="small"
                                    @click='SendToKYC()' :loading="loading" :disabled="loading || !selectedDocumentId || !selectedFaceId || !verificationType" />
                            </div>
                        </div>
                        <div v-if="!kycStatusLoading" class="flex justify-content-between gap-2">
                            <Button v-if="!kycPositive" label="Редактировать" severity="info" outlined @click="step=1" :disabled="loading" icon="pi pi-pencil" class="text-sm"/>
                            <Button v-if="!kycPositive" label="Начать сначала" severity="info" @click="openConfirm" :disabled="loading || kycStatusLoading" class="text-sm" icon="pi pi-arrow-right"/>
                        </div>
                    </div>

                    <!-- Шаг 1: основные данные -->
                    <div v-if="step===1" class="flex flex-column w-full gap-3">
                        <Message severity="info" size="small" icon="pi pi-user" @click="dialog=true" class="cursor-pointer">
                            Какие данные нужны для прохождения верификации?
                        </Message>
                        
                        <div class="flex flex-column w-full gap-2">
                            <span class="flex">Полное имя: </span>
                            <InputText v-model="client.first_name" class="flex" placeholder="Иван" @blur="v$.client.first_name.$touch()" />
                            <small v-if="v$.client.first_name.$error" class="text-red-500">Укажите имя</small>
                        </div>

                        <div class="flex flex-column w-full gap-2">
                            <span class="flex">Фамилия: </span>
                            <InputText v-model="client.last_name" class="flex" placeholder="Иванов" @blur="v$.client.last_name.$touch()" />
                            <small v-if="v$.client.last_name.$error" class="text-red-500">Укажите фамилию</small>
                        </div>

                        <div class="flex flex-column w-full gap-2">
                            <span class="flex">Email: </span>
                            <InputText v-model="client.email" class="flex" placeholder="ivan@example.com" @blur="v$.client.email.$touch()" />
                            <small v-if="v$.client.email.$error" class="text-red-500">Введите корректный email</small>
                        </div>

                        <div class="flex flex-column w-full gap-2">
                            <span class="flex">Номер телефона: </span>
                            <InputNumber v-model="client.phone" class="flex" placeholder="79998887766" :useGrouping="false" @blur="v$.client.phone.$touch()" />
                            <small v-if="v$.client.phone.$error" class="text-red-500">Введите телефон (10–15 цифр)</small>
                        </div>

                        <div v-if="!hasClientId" class="w-full flex mt-2 flex justify-content-between flex-wrap">
                            <Button label="Сохранить и продолжить" 
                                class="flex w-full border-round-lg text-sm" :loading="loading" :disabled="loading || !isFormValid" severity="info"
                                @click='AddClient()' />
                        </div>
                        <div v-else class="w-full flex mt-2 flex justify-content-between flex-wrap gap-3">
                            <Button label="Сохранить и продолжить" 
                                class="flex w-full border-round-lg text-sm" :loading="loading" :disabled="loading || !isFormValid" severity="info"
                                @click='AddClient()' />
                            <Button label="Продолжить" @click='step=2' 
                                class="flex w-full border-round-lg" :loading="loading" :disabled="loading" severity="info" outlined />
                        </div>
                    </div>

                    <!-- Шаг 2: документ -->
                    <div v-if="step===2" class="flex flex-column w-full gap-3">
                        <span class="flex text-md font-medium">Загрузите фото документа</span> 
                        <div v-if="filesDocument.length>0" class="flex w-full gap-2">
                            <div v-for="file in filesDocument" :key="file.id"
                                class="flex flex-column gap-2 p-2 border-1 surface-border border-round-lg w-6rem">
                                <div class="flex flex-column">
                                    <img :src="file.url" v-if="file.url && (file.mimetype=='image/png' || file.mimetype=='image/jpg' || file.mimetype=='image/jpeg')"
                                        class="w-full border-1 surface-border border-round-lg mt-2" />
                                </div>
                                <div class="flex flex-row align-items-start gap-2">
                                    <RadioButton :inputId="'doc-'+file.id" :value="file.id" v-model="selectedDocumentId" :checked="selectedDocumentId === file.id" />
                                </div>
                            </div>
                        </div>
                        <UploadFilesPublic :multiple="false" :maxFiles="1" :data="{ client_id: clientId, from: 'user', type: 'document' }"
                            @onUpload="files => onUpload(files, 'document')" @onReset="() => resetFiles('document')" @onLoading="setLoading" 
                            :userLogin="userLogin" />
                        <div class="flex justify-content-between gap-2">
                            <Button label="Назад" @click="step=1" :disabled="loading" 
                                class="flex border-round-lg" severity="info" outlined />
                            <Button label="Продолжить" @click="step=3" :disabled="!selectedDocumentId || loading"
                                class="flex w-full border-round-lg flex-1" severity="info" />
                        </div>
                    </div>

                    <!-- Шаг 3: лицо -->
                    <div v-if="step===3" class="flex flex-column w-full gap-3">
                        <span class="flex text-md font-medium">Загрузите фото с лицом</span>
                        <div v-if="filesFace.length>0" class="flex w-full gap-2">
                            <div v-for="file in filesFace" :key="file.id"
                                class="flex flex-column gap-2 p-2 border-1 surface-border border-round-lg w-6rem">
                                <div class="flex flex-column">
                                    <img :src="file.url" v-if="file.url && (file.mimetype=='image/png' || file.mimetype=='image/jpg' || file.mimetype=='image/jpeg')"
                                        class="w-full border-1 surface-border border-round-lg mt-2" />
                                </div>
                                <div class="flex flex-row align-items-start gap-2">
                                    <RadioButton :inputId="'face-'+file.id" :value="file.id" v-model="selectedFaceId" />
                                </div>
                            </div>
                        </div>
                        <UploadFilesPublic :multiple="false" :maxFiles="1" :data="{ client_id: clientId, from: 'user', type: 'face' }"
                            @onUpload="files => onUpload(files, 'face')" @onReset="() => resetFiles('face')" @onLoading="setLoading"
                            :userLogin="userLogin" />
                        <div class="flex justify-content-between gap-2">
                            <Button label="Назад" @click="step=2" :disabled="loading" 
                                class="flex border-round-lg" severity="info" outlined />
                            <Button label="Продолжить" @click="goNextFromFace()" :disabled="!selectedFaceId || loading"
                                class="flex w-full border-round-lg flex-1" severity="info" />
                        </div>
                    </div>

                    <!-- Шаг 4: прописка (обязательно для avangard) -->
                    <div v-if="step===4 && verificationType==='avangard'" class="flex flex-column w-full gap-3">
                        <span class="flex text-md font-medium">Загрузите фото страницы прописки (при необходимости)</span>
                        <div v-if="filesRegistration.length>0" class="flex w-full gap-2">
                            <div v-for="file in filesRegistration" :key="file.id"
                                class="flex flex-column gap-2 p-2 border-1 surface-border border-round-lg w-6rem">
                                <div class="flex flex-column">
                                    <img :src="file.url" v-if="file.url && (file.mimetype=='image/png' || file.mimetype=='image/jpg' || file.mimetype=='image/jpeg')"
                                        class="w-full border-1 surface-border border-round-lg mt-2" />
                                </div>
                                <div class="flex flex-row align-items-start gap-2">
                                    <RadioButton :inputId="'registration-'+file.id" :value="file.id" v-model="selectedRegistrationId" />
                                </div>
                            </div>
                        </div>
                        <UploadFilesPublic :multiple="false" :maxFiles="1" :data="{ client_id: clientId, from: 'user', type: 'registration' }"
                            @onUpload="files => onUpload(files, 'registration')" @onReset="() => resetFiles('registration')" @onLoading="setLoading" 
                            :userLogin="userLogin"/>
                        <div class="flex justify-content-between gap-2">
                            <Button label="Назад" @click="step=3" :disabled="loading" 
                                class="flex border-round-lg" severity="info" outlined />
                            <Button label="Продолжить" @click="step=5" :disabled="loading" 
                                class="flex w-full border-round-lg flex-1" severity="info"/>
                        </div>
                    </div>

                    <!-- Шаг 5: отправка на KYC -->
                    <div v-if="step===5" class="flex flex-column w-full gap-3">
                        <Message severity="success" size="small" icon="pi pi-send">Проверьте данные перед отправкой</Message>
                        <div class="flex flex-column gap-2 p-3 border-1 surface-border border-round-lg">
                            <div class="flex flex-row justify-content-between">
                                <span class="text-600">Имя</span>
                                <span class="text-900">{{ client.first_name }}</span>
                            </div>
                            <div class="flex flex-row justify-content-between">
                                <span class="text-600">Фамилия</span>
                                <span class="text-900">{{ client.last_name }}</span>
                            </div>
                            <div class="flex flex-row justify-content-between">
                                <span class="text-600">Email</span>
                                <span class="text-900">{{ client.email }}</span>
                            </div>
                            <div class="flex flex-row justify-content-between">
                                <span class="text-600">Телефон</span>
                                <span class="text-900">{{ client.phone }}</span>
                            </div>
                            <Divider/>
                            <div class="flex flex-row justify-content-between">
                                <span class="text-600">Фото паспорта</span>
                                <span class="text-900">{{ (selectedDocumentId) ? 'ок' : 'не загружен' }}</span>
                            </div>
                            <div class="flex flex-row justify-content-between">
                                <span class="text-600">Фото лица</span>
                                <span class="text-900">{{ (selectedFaceId) ? 'ок' : 'не загружен' }}</span>
                            </div>
                            <div v-if="verificationType=='avangard'" class="flex flex-row justify-content-between">
                                <span class="text-600">Фото прописки</span>
                                <span class="text-900">{{ (selectedRegistrationId) ? 'ок' : 'не загружен' }}</span>
                            </div>
                        </div>
                        <div class="flex justify-content-between gap-2">
                            <Button label="Назад" @click="step=4" :disabled="loading" 
                                class="flex border-round-lg" severity="secondary" outlined/>
                            <Button label="Отправить" icon="pi pi-arrow-right" class="flex w-full border-round-lg flex-1" severity="success" iconPos="right"
                                @click='SendToKYC()' :loading="loading" :disabled="loading || !selectedDocumentId || !selectedFaceId || !verificationType" />
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </Page>

    <Dialog v-model:visible="dialog" modal class="border-none">
        <template #header>
            <div class="flex flex-column w-full mb-3">
                <span class="flex text-lg font-medium">Информация по KYC</span>
            </div>
        </template>
        <KycInfo />
    </Dialog> 
</template>

<script>
import Connect from '@/assets/js/connect.js'
import Helper from '@/assets/js/helper.js'
import { useRoute } from 'vue-router'
import UploadFilesPublic from '@/components/public/upload_files_public.vue'
import { useVuelidate } from '@vuelidate/core'
import { required, email, helpers } from '@vuelidate/validators'
import Page from '@/components/public/page.vue'
import KycInfo from '@/components/public/widgets/kyc_info.vue'

export default {
    components: { UploadFilesPublic, Page, KycInfo },
    props: {},
    setup() {
        return { v$: useVuelidate() }
    },
    data() {
        return {
            step: 1,
            loading: false,
            dialog: false,
            clientId: null,
            client: {
                user_login: null,
                id: null,
                first_name: '',
                last_name: '',
                email: '',
                phone: ''
            },
            files: [],
            selectedDocumentId: null,
            selectedFaceId: null,
            selectedRegistrationId: null,
            userLogin: null,
            verificationType: null,
            kycStatusLoading: false,
            kycPositive: false,
            kycStatus: null,
            kycErrorMessage: null,
            steps: [
                {
                    label: 'Статус',
                    value: 0
                },
                {
                    label: 'Контакты',
                    value: 1
                },
                {
                    label: 'Паспорт',
                    value: 2
                },
                {
                    label: 'Фото',
                    value: 3
                },
                {
                    label: 'Прописка',
                    value: 4
                },
                {
                    label: 'KYC',
                    value: 5
                }
            ],
            verifTypes: ['default', 'avangard'],
            $confirm: {
                require: {
                    header: 'Подтвердите действие',
                    icon: 'pi pi-exclamation-triangle',
                    acceptLabel: 'Да',
                    rejectLabel: 'Нет',
                    acceptButtonStyleClass: 'p-button-danger',
                }
            }
        }
    },
    validations() {
        const isPhone = helpers.withMessage(
            'Введите телефон (10–15 цифр)',
            value => /^[0-9]{10,15}$/.test(String(value ?? ''))
        )
        return {
            client: {
                user_login: {},
                id: {},
                first_name: { required },
                last_name: { required },
                email: { required, email },
                phone: { required, isPhone },
            }
        }
    },
    computed: {
        screenSize() { return this.$store.state.screenSize },
        filesDocument() { return this.files.filter(f => f.type === 'document' || !f.type) },
        filesFace() { return this.files.filter(f => f.type === 'face' || !f.type) },
        filesRegistration() { return this.files.filter(f => f.type === 'registration' || !f.type) },
        kycClientId() { return this.$store.getters.StateKycClientId },
        kycClient() { return this.$store.getters.StateKycClient },
        hasClientId() { return !!this.kycClientId },
        kycPartnerLogin() { return this.$store.getters.StateKycPartnerLogin },
        percent() { return this.getPercentFromStep(this.step) },
        isFormValid() {
            return !this.v$.$invalid
        }
    },
    methods: {
        showMess(severity, summary, detail) {
            this.$toast.add({severity, summary, detail, life: 5000});
        },
        openConfirm(){
            this.$confirm.require({
                message: 'Сбросить и начать сначала?',
                header: 'Подтвердите действие',
                icon: 'pi pi-exclamation-triangle',
                rejectProps: {
                    label: 'Нет',
                    severity: 'secondary',
                    outlined: true
                },
                acceptProps: {
                    label: 'Да',
                    severity: 'info'
                },
                accept: () => {
                    this.ResetData()
                },
                reject: () => {}
            })
        },
        getPercentFromStep(step){
            const totalSteps = 5
            return Math.round((step / totalSteps) * 100)
        },
        getVerifTypeByCode(code){
            return (this.verifTypes.includes(code)) ? code : 'default'
        },
        goNextFromFace(){
            if(this.verificationType === 'avangard') this.step = 4
            else this.step = 5
        },
        getKycStatus(status){
            return this.helper.getKycStatus(status)
        },
        getKycSeverity(status){
            return this.helper.getKycSeverity(status)
        },
        async GetPersonalFiles() {
            if(!this.clientId) return
            try {
                const resFiles = await this.connect.GetPublicFiles(this.userLogin, this.clientId, 100, 0, {}).then(res=>res)
                if(resFiles && resFiles.result && Array.isArray(resFiles.data)){
                    // Если тип отсутствует, оставляем как есть; отобразим файл на шагах с фильтром по типу или без типа
                    this.files = resFiles.data.map(f => ({ ...f }))
                    // Автоматически выбираем первый подходящий файл по типу, если выбор не сделан
                    const firstDocument = this.files.find(f => f.type === 'document' && f.id)
                    const firstFace = this.files.find(f => f.type === 'face' && f.id)
                    const firstRegistration = this.files.find(f => f.type === 'registration' && f.id)

                    if(!this.selectedDocumentId && firstDocument){
                        this.selectedDocumentId = firstDocument.id
                        try { this.$store.commit('setKycDocumentFileId', firstDocument.id) } catch {}
                    }
                    if(!this.selectedFaceId && firstFace){
                        this.selectedFaceId = firstFace.id
                        try { this.$store.commit('setKycFaceFileId', firstFace.id) } catch {}
                    }
                    if(!this.selectedRegistrationId && firstRegistration){
                        this.selectedRegistrationId = firstRegistration.id
                        try { this.$store.commit('setKycRegistrationFileId', firstRegistration.id) } catch {}
                    }
                }
            } catch(e) { }
        },
        async AddClient(){
            this.loading = true
            if(!this.isFormValid){
                this.showMess('error', 'Ошибка', 'Заполните корректно обязательные поля')
                this.loading = false
                return
            }
            console.log('Adding client', this.client)
            try{
                const add = await this.connect.AddPublicClient(this.client).then(res=>res)
                if(add && add.result && add.data){
                    // Пытаемся извлечь идентификатор клиента из разных возможных полей
                    this.clientId = add.data.id || add.data._id || add.data.client_id || add.data
                    if(!this.clientId){
                        this.showMess('warn', 'Внимание', 'Клиент создан, но id не получен')
                    }
                    try {
                        this.$store.commit('setKycClientInfo', this.client)
                        this.$store.commit('setKycClientId', this.clientId)
                    } catch {}
                    this.showMess('success', 'Успешно', 'Данные сохранены')
                    this.step = 2
                } else {
                    this.showMess('error', 'Ошибка', add?.message || 'Ошибка при добавлении клиента')
                }
            } catch(e){
                this.showMess('error', 'Ошибка', 'Ошибка при добавлении клиента')
                console.log('error', e)
            }
            this.loading = false
        },
        onUpload(files, fileType){
            if(!files || files.length===0) return
            for (let i = 0; i < files.length; i++) {
                const f = files[i]
                if(f.id){
                    const newF = { ...f, type: fileType }
                    this.files.push(newF)
                    if(fileType==='document') this.selectedDocumentId = f.id
                    if(fileType==='face') this.selectedFaceId = f.id
                    if(fileType==='registration') this.selectedRegistrationId = f.id
                    try {
                        if(fileType==='document') this.$store.commit('setKycDocumentFileId', f.id)
                        if(fileType==='face') this.$store.commit('setKycFaceFileId', f.id)
                        if(fileType==='registration') this.$store.commit('setKycRegistrationFileId', f.id)
                    } catch {}
                } else {
                    this.showMess('error', 'Ошибка', 'Загруженный файл не содержит идентификатор')
                }
            }
        },
        resetFiles(fileType){
            this.files = this.files.filter(f => f.type !== fileType)
            if(fileType==='document') this.selectedDocumentId = null
            if(fileType==='face') this.selectedFaceId = null
            if(fileType==='registration') this.selectedRegistrationId = null
        },
        setLoading(val){ this.loading = val },
        async SendToKYC(){
            if (!this.selectedDocumentId || !this.selectedFaceId) {
                this.showMess('error', 'Ошибка', 'Выберите файл документа и файл с лицом')
                return
            }
            if (!this.verificationType) {
                this.showMess('error', 'Ошибка', 'Выберите тип верификации')
                return
            }
            if (this.verificationType === 'avangard' && !this.selectedRegistrationId) {
                this.showMess('error', 'Ошибка', 'Для Avangard требуется фото страницы прописки')
                return
            }
            this.loading = true
            try{
                try { this.$store.commit('setKycVerificationType', this.verificationType) } catch {}
                const data = {
                    user_login: this.userLogin || null,
                    client_id: this.clientId,
                    verification_type: this.verificationType,
                    document_file_id: this.selectedDocumentId,
                    face_file_id: this.selectedFaceId,
                    registration_file_id: this.selectedRegistrationId || null
                }
                const add = await this.connect.PublicSendToKyc(data).then(res=>res)
                if(add && add.result && add.data){
                    this.showMess('success', 'Успешно', 'Верификация отправлена')
                    this.step = 0
                    this.kycPositive = true
                } else {
                    this.showMess('error', 'Ошибка', add?.message || 'Ошибка при отправке на KYC')
                }
            } catch(e){
                this.showMess('error', 'Ошибка', 'Ошибка при отправке на KYC')
                console.log('error', e)
            }
            this.loading = false
        },
        async CheckKycStatus(){
            if(!this.clientId){
                this.kycStatus = 'not_sent'
                this.kycPositive = false
                return
            }
            if(!this.userLogin){
                return
            }
            if(this.kycStatusLoading) return
            const data = {
                client_id: this.clientId,
                user_login: this.userLogin
            }
            this.kycStatusLoading = true
            try {
                const res = await this.connect.PublicKycStatus(data).then(res=>res)
                if(res && res.result){
                    this.kycPositive = true
                    this.kycStatus = res.data.status
                    this.kycErrorMessage = (res.data.message && this.kycStatus != 'approved') ? res.data.message || '' : ''
                } else {
                    this.kycStatus = res?.message || 'Не удалось получить статус'
                    this.kycPositive = false
                }
            } catch(e){
                this.kycStatus = 'not_sent'
                this.kycPositive = false
            }
            this.kycStatusLoading = false
        },
        async ResetData(){
            this.step = 1
            this.clientId = null
            this.client = {
                user_login: this.userLogin || null,
                id: null,
                first_name: '',
                last_name: '',
                email: '',
                phone: ''
            }
            this.files = []
            this.selectedDocumentId = null
            this.selectedFaceId = null
            this.selectedRegistrationId = null
        }
    },
    async beforeCreate(){
        this.connect = new Connect()
        this.helper = new Helper()
    },
    mounted(){
        const route = useRoute()
        const routerKycType = (route.params && route.params.kyc_type) ? String(route.params.kyc_type) : null
        const routerUserLogin = (route.params && route.params.user_login) ? String(route.params.user_login) : null
        this.verificationType = this.getVerifTypeByCode(routerKycType)
        try { this.$store.commit('setKycVerificationType', this.verificationType) } catch {}

        if(routerUserLogin) {
            try { this.$store.commit('setKycPartnerLogin', routerUserLogin) } catch {}
            this.userLogin = routerUserLogin
            this.client.user_login = routerUserLogin
            if(this.hasClientId){
                this.clientId = this.kycClientId
                this.GetPersonalFiles()
                this.step = 0
                this.CheckKycStatus()
            }
         }
        if(this.kycClient){
            this.client = {
                user_login: this.userLogin || null,
                id: this.kycClientId || null,
                first_name: this.kycClient.first_name || '',
                last_name: this.kycClient.last_name || '',
                email: this.kycClient.email || '',
                phone: this.kycClient.phone || ''
            }
        }
    }
}
</script>